import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { MapPin, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { exportCsv, exportGlobalCsv } from '../../store/reporting/actions';
import { useTranslation } from 'react-i18next';

const ExportLocationsForm = ({ organizationId, mode = 'org' }) => {
  const isGlobal = mode === 'global';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { exportLoading } = useSelector(state => state.Reporting);
  const [format, setFormat] = useState('csv');

  const handleExport = () => {
    if (isGlobal) {
      dispatch(exportGlobalCsv('locations'));
      return;
    }
    if (!organizationId) return;
    dispatch(exportCsv('locations', { organizationId }));
  };

  return (
    <div className="p-4">
      <div className="d-flex align-items-center gap-2 mb-4">
        <MapPin size={20} className="text-primary" />
        <h5 className="mb-0 fw-bold">{t('reporting.export.locations_title', 'Export Location List')}</h5>
      </div>

      <p className="text-muted mb-4">
        {isGlobal
          ? t('reporting.export.locations_description_global', 'Export all active locations across all your organizations including parent location hierarchy and addresses. The first column identifies which organization each row belongs to.')
          : t('reporting.export.locations_description', 'Export all active locations for your organization including parent location hierarchy and addresses.')}
      </p>

      {/* Export Format */}
      <div className="mb-4">
        <label className="form-label fw-semibold">{t('shared.export_format', 'Export Format')}</label>
        <div className="d-flex gap-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="locationExportFormat"
              id="locationFormatCsv"
              value="csv"
              checked={format === 'csv'}
              onChange={(e) => setFormat(e.target.value)}
            />
            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="locationFormatCsv">
              <FileSpreadsheet size={16} />
              {t('shared.csv', 'CSV')}
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="locationExportFormat"
              id="locationFormatPdf"
              value="pdf"
              disabled
            />
            <label className="form-check-label d-flex align-items-center gap-1 text-muted" htmlFor="locationFormatPdf">
              <FileText size={16} />
              {t('shared.pdf', 'PDF')} <span className="badge bg-light text-muted ms-1">{t('shared.coming_soon', 'Coming soon')}</span>
            </label>
          </div>
        </div>
      </div>

      {/* Export Button */}
      <motion.button
        className="btn btn-primary d-flex align-items-center gap-2"
        onClick={handleExport}
        disabled={exportLoading || (!isGlobal && !organizationId)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {exportLoading ? (
          <>
            <span className="spinner-border spinner-border-sm" />
            {t('shared.exporting', 'Exporting...')}
          </>
        ) : (
          <>
            <Download size={18} />
            {t('reporting.export.button_export_locations', 'Export Locations')}
          </>
        )}
      </motion.button>
    </div>
  );
};

export default ExportLocationsForm;
